import { ObjectId } from '@racemap/sdk/schema/base';
import { type EnrichedTracker, EnrichedTrackerSchema } from '@racemap/sdk/schema/trackers';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { USER_ROLE } from '@racemap/utilities/consts/trackers';
import { type Immutable, produce } from 'immer';
import type { DateTime } from 'luxon';
import { Tristate } from 'src/components/utils/tristateTask';
import { toast } from 'src/lib/toast';

import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createUserRoleActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  TrackersActions,
  | 'removeUserFromTracker'
  | 'removeUsersFromTrackers'
  | 'updateUserRolesInTrackers'
  | 'addUserToTracker'
> => ({
  removeUserFromTracker: async (trackerId, userId, userRole, dates) => {
    const tracker = get().trackers.items.get(trackerId.toHexString());
    if (tracker == null) return;

    switch (userRole) {
      case USER_ROLE.EDITOR: {
        const newEditors = tracker.editors.filter((editor) => !editor.equals(userId));
        get().trackers.actions.updateTracker(trackerId, { editors: newEditors });
        break;
      }
      case USER_ROLE.BORROWER: {
        const newBorrowers = tracker.borrowers.filter(
          (borrower) =>
            !(
              borrower.id.equals(userId) &&
              borrower.startTime.valueOf() === dates?.startTime.valueOf() &&
              borrower.endTime.valueOf() === dates?.endTime.valueOf()
            ),
        );
        get().trackers.actions.updateTracker(trackerId, {
          borrowers: newBorrowers,
        });
        break;
      }
      case USER_ROLE.OWNER: {
        // TODO: remove the owner id really here
        const currentUserId = get().users.currentUserId;
        if (currentUserId == null) return;

        get().trackers.actions.updateTracker(trackerId, { owner: new ObjectId(currentUserId) });
        break;
      }
    }
  },
  removeUsersFromTrackers: async (trackerIds: Array<ObjectId>, userIds: Array<ObjectId>) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );

      await apiClient.removeUsersFromTrackers(
        trackerIds.map((id) => id.toHexString()),
        userIds.map((id) => id.toHexString()),
      );

      set(
        produce((s: DraftState) => {
          for (const tId of trackerIds) {
            const idStr = tId.toHexString();
            const tracker = s.trackers.items.get(idStr);
            if (!tracker) continue;

            if (tracker.owner && userIds.some((uId) => tracker.owner.equals(uId))) {
              tracker.owner = new ObjectId(get().users.getter.currentUser()?.id || '');
            }

            tracker.editors = tracker.editors.filter(
              (editor) => !userIds.some((uId) => editor.equals(uId)),
            );

            tracker.borrowers = tracker.borrowers.filter(
              (borrower) => !userIds.some((uId) => borrower.id.equals(uId)),
            );

            s.trackers.items.set(idStr, tracker);
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      toast.success('Users successfully removed from the tracker(s).');
    } catch (error) {
      console.error('Failed to remove users from trackers:', error);
      toast.error('The attempt to remove users from the trackers failed.');
    }
  },
  updateUserRolesInTrackers: async (
    userIds: Array<string>,
    trackers: Immutable<Array<EnrichedTracker>>,
    newRole: USER_ROLE,
    startTime?: DateTime,
    endTime?: DateTime,
  ) => {
    const toastId = toast('Updating user roles...', {
      autoClose: false,
      isLoading: true,
      progress: 0,
      type: 'info',
      closeButton: false,
      draggable: false,
      position: 'top-center',
    });
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );

      const trackerIds = trackers.map((t) => t.id.toHexString());
      const result = await apiClient.updateUserRolesInTrackers(
        trackerIds,
        userIds,
        newRole,
        newRole === USER_ROLE.BORROWER ? startTime?.toJSDate() || undefined : undefined,
        newRole === USER_ROLE.BORROWER ? endTime?.toJSDate() || undefined : undefined,
      );

      set(
        produce((s: DraftState) => {
          for (const updatedTracker of result) {
            const trackerParsed = EnrichedTrackerSchema.parse(updatedTracker);
            s.trackers.items.set(trackerParsed.id.toHexString(), trackerParsed);
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      toast.update(toastId, {
        render: 'User roles successfully updated in the tracker(s).',
        isLoading: false,
        type: 'success',
        autoClose: 2 * OneSecondInMillis,
        hideProgressBar: true,
      });
    } catch (error) {
      let errorMessage = "The user roles can't be updated in the tracker(s)!";

      if (error instanceof HTTPFetchError) {
        errorMessage += ` ${error.serverError}`;
      } else if (error instanceof Error) {
        errorMessage += ` ${error.message}`;
      }

      toast.update(toastId, {
        render: errorMessage,
        isLoading: false,
        type: 'error',
        autoClose: 2 * OneSecondInMillis,
        hideProgressBar: true,
      });
      console.log(error);
    }
  },

  addUserToTracker: async ({ trackerIds, userId, role, startTime, endTime }) => {
    try {
      set(
        produce((s: DraftState) => {
          s.trackers.savingState = Tristate.SPINNING;
        }),
      );

      const result = await apiClient.addUserToTrackers(
        trackerIds.map((t) => t.toHexString()),
        userId.toHexString(),
        role,
        startTime?.toISO() || undefined,
        endTime?.toISO() || undefined,
      );

      set(
        produce((s: DraftState) => {
          for (const updatedTracker of result) {
            const trackerParsed = EnrichedTrackerSchema.parse(updatedTracker);
            s.trackers.items.set(trackerParsed.id.toHexString(), trackerParsed);
          }
          s.trackers.savingState = Tristate.NORMAL;
        }),
      );
      toast.success('User successfully added to the tracker(s).');
    } catch (error) {
      let errorMessage = "The user can't be added to the tracker(s)!";

      if (error instanceof HTTPFetchError) {
        errorMessage += ` ${error.serverError}`;
      } else if (error instanceof Error) {
        errorMessage += ` ${error.message}`;
      }

      toast.error(errorMessage);
      console.log(error);
    }
  },
});
